import { cn } from "@/lib/utils";

/**
 * A titled block used by inner pages. Consistent vertical rhythm.
 * Optional `tint` prop switches background: 'white' (default) | 'cream' | 'deep'.
 */
export function InnerBlock({ tint = "white", eyebrow, heading, subheading, children, className }) {
  const bg = tint === "cream" ? "bg-wafa-cream/40" : tint === "deep" ? "bg-wafa-deep text-white" : "bg-white";
  const isDark = tint === "deep";
  return (
    <section className={cn(bg, className)}>
      <div className="mx-auto max-w-6xl px-4 py-16 lg:px-6 lg:py-24">
        {eyebrow ? (
          <div className={cn("text-xs font-semibold uppercase tracking-widest", isDark ? "text-wafa-sunshine" : "text-wafa-primary")}>
            {eyebrow}
          </div>
        ) : null}
        {heading ? (
          <h2 className={cn("mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl", isDark ? "text-white" : "text-wafa-deep")}>
            {heading}
          </h2>
        ) : null}
        {subheading ? (
          <p className={cn("mt-5 max-w-3xl text-base leading-relaxed lg:text-lg", isDark ? "text-white/85" : "text-wafa-ink/80")}>
            {subheading}
          </p>
        ) : null}
        <div className="mt-10">{children}</div>
      </div>
    </section>
  );
}

/**
 * Grid of small "title + one-line body" cards used everywhere.
 */
export function GridCards({ items, columns = 3, tint }) {
  const cols = columns === 2 ? "md:grid-cols-2" : columns === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-2 lg:grid-cols-3";
  const dark = tint === "deep";
  return (
    <div className={cn("grid gap-4", cols)}>
      {items.map((it, i) => (
        <div
          key={i}
          data-testid={`grid-card-${i}`}
          className={cn(
            "rounded-lg border p-5 transition-all duration-200 hover:-translate-y-1",
            dark
              ? "border-white/15 bg-white/5 text-white hover:border-white/30"
              : "border-wafa-deep/10 bg-white hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.2)]",
          )}
        >
          <div className={cn("font-heading font-semibold", dark ? "text-white" : "text-wafa-deep")}>{it.title}</div>
          {it.desc ? (
            <div className={cn("mt-2 text-sm", dark ? "text-white/75" : "text-wafa-ink/70")}>{it.desc}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * Bullet list used for "What comes as standard" and similar.
 */
export function BulletList({ items }) {
  return (
    <ul className="grid gap-2.5 md:grid-cols-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-wafa-ink/85 lg:text-base">
          <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-wafa-primary" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Numbered process/step list.
 */
export function NumberedList({ items }) {
  return (
    <ol className="space-y-5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-wafa-cream text-sm font-semibold text-wafa-deep">
            {i + 1}
          </span>
          <div className="pt-1 text-sm text-wafa-ink/85 lg:text-base">{it}</div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Highlight quote / callout — cream tinted block with a left green rule.
 */
export function Callout({ children, dark }) {
  return (
    <div
      className={cn(
        "rounded-lg border-s-4 px-5 py-4 text-base leading-relaxed lg:text-lg",
        dark ? "border-wafa-sunshine bg-white/5 text-white" : "border-wafa-primary bg-wafa-cream/60 text-wafa-deep",
      )}
    >
      {children}
    </div>
  );
}
